import { MAX_DAILY_QUARTER_HOURS } from '../calendar/constants';
import { BusinessDate } from '../date/business-date';
import { Hours } from '../shared/hours';
import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type {
  ActivityId,
  ExecutionResourceId,
  ProjectId,
  ServiceId,
  TeamId,
  WeeklyAllocationId,
  WeeklyPlanId,
} from '../shared/ids';
import { PLANNING_ERROR, PLANNING_ERROR_MESSAGE } from './errors';

/**
 * Alocação de um dia — "na segunda, o João trabalha 4h na Alvenaria do 2º".
 *
 * A unidade de lançamento é o **dia**, não a semana. É assim que o encarregado monta o
 * serviço — "segunda: 4h de alvenaria e 5h de estrutura" — e é o que torna o conflito de
 * capacidade verificável de verdade: a jornada existe por dia (9h), e um total semanal
 * dentro do limite pode esconder uma segunda-feira de 12h.
 *
 * A semana continua sendo a unidade de **planejamento** (RN-080): o plano agrupa os dias,
 * o saldo exibido é o da semana, e é a semana que se publica e se fecha.
 *
 * As horas são **quartos de hora inteiros** (ADR-106). A granularidade de 15 minutos da
 * RN-048 deixa de ser validação e vira impossibilidade de representação: não existe
 * alocação de 7 minutos porque o tipo não sabe escrevê-la, e 45h somadas em incrementos
 * de 15 minutos fecham em 45h exatas — nunca em 44,99999999999999h.
 *
 * `exceedsCapacity` é dado, não cálculo: registra que **naquele momento** a alocação
 * passou do limite e foi autorizada com justificativa (RN-045). O saldo de hoje pode
 * mudar — um feriado inserido depois, uma alocação removida — e a exceção autorizada
 * continua sendo um fato do histórico.
 */
export interface WeeklyAllocation {
  readonly id: WeeklyAllocationId;
  readonly weeklyPlanId: WeeklyPlanId;
  readonly projectId: ProjectId;
  readonly resourceId: ExecutionResourceId;
  readonly activityId: ActivityId;
  /**
   * O serviço do dia — o que a obra chama de atividade (ADR-135).
   *
   * A frente diz **onde** (pavimento × disciplina); o serviço diz **o quê**. Ele entra na
   * identidade da alocação, e por isso o mesmo dia na mesma frente cabe em várias linhas:
   * quatro horas de fôrma e quatro de armadura são dois trabalhos, com metas em unidades
   * diferentes (RN-231).
   *
   * **Ausente é legítimo, e é buraco visível de propósito.** Três caminhos o deixam vazio:
   * a alocação gravada antes desta fase, o lançamento do RDO numa frente sem serviço
   * cadastrado (ADR-133) e a proposta automática numa disciplina com mais de um serviço —
   * ela não tem como escolher, e chutar inventaria histórico. Linha sem serviço guarda
   * horas e não mede: quem precisar dela medindo apaga e refaz.
   */
  readonly serviceId?: ServiceId;
  /** O dia trabalhado, `"YYYY-MM-DD"`. Sempre dentro da semana do plano (RN-080). */
  readonly date: string;
  readonly quarterHours: number;
  /**
   * Quanto se espera produzir neste dia, na unidade da atividade.
   *
   * Mora aqui, e não na atividade, porque é por pessoa: dois pedreiros na mesma frente
   * têm metas diferentes. O total da semana é a soma dos dias — projeção, não um segundo
   * número guardado.
   */
  readonly plannedQuantity?: number;
  /**
   * Quanto foi produzido de fato, lançado na conferência.
   *
   * Fica na alocação pela mesma razão que o ajuste de horas fica: duplicar a linha numa
   * tabela de "itens conferidos" criaria dois lugares para a mesma verdade, e o segundo é
   * sempre o que fica desatualizado.
   */
  readonly executedQuantity?: number;
  /**
   * Quanto do executado não está conforme (Fase 37, RN-475): a maior quantidade das NCs vivas
   * desta medição. É uma parte do executado, e sai da produção — o avanço, a produtividade e o
   * painel usam `acceptedQuantity`. Quem a mantém é a gravação das NCs, nunca a tela.
   */
  readonly nonConformingQuantity?: number;
  /**
   * A equipe de cujo planejamento a alocação nasceu (Fase 30, RN-422).
   *
   * É uma **marca**, e não a dona da alocação: as horas, a jornada, a hora extra e a meta
   * continuam sendo desta pessoa, e nenhum consumidor da alocação — a conferência, o RDO, o
   * avanço, o painel, a produtividade, a folha — muda por causa dela. Ela só diz que a pessoa
   * está ali porque a equipe foi alocada: é por ela que a grade junta as pessoas na linha da
   * equipe (D10) e que a troca de equipe leva os dias seguintes junto (RN-426).
   *
   * Ausente é a alocação de sempre, feita pessoa a pessoa.
   */
  readonly plannedTeamId?: TeamId;
  /** Autorizada acima da capacidade (RN-045). */
  readonly exceedsCapacity: boolean;
  /** Obrigatória quando `exceedsCapacity` (RN-045). */
  readonly justification?: string;
  /** Registrado quando se aloca em atividade impedida (RN-085). */
  readonly acknowledgedRestriction?: boolean;
  readonly notes?: string;
  /** Bloqueio otimista: quem grava com versão vencida é recusado, não sobrescreve. */
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** Exclusão é soft delete auditado (RN-088). Alocação excluída não conta (RN-049). */
  readonly deletedAt?: string;
}

/**
 * Valida horas contra a RN-048 — a mesma função para tela, caso de uso e importação.
 *
 * Os três limites em uma passagem só, com erro distinto para cada um: "maior que zero",
 * "múltiplo de 15 minutos" e "acima do teto físico" são problemas diferentes e merecem
 * mensagens diferentes.
 *
 * O teto é o do **dia**: a alocação cobre uma data, e 24h é o limite físico do que se
 * pode escrever nela (RN-048). Ele existe para que `40` digitado no lugar de `4` pare na
 * validação de forma; quem recusa 10h numa jornada de 9h é a capacidade, logo adiante.
 */
export function parseAllocationHours(
  quarterHours: number,
  maxQuarterHours: number = MAX_DAILY_QUARTER_HOURS,
): Result<number, DomainError> {
  if (!Number.isInteger(quarterHours)) {
    return err(
      domainError(
        PLANNING_ERROR.ALLOCATION_HOURS_GRANULARITY,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.ALLOCATION_HOURS_GRANULARITY]!,
        { quarterHours },
      ),
    );
  }
  if (quarterHours <= 0) {
    return err(
      domainError(
        PLANNING_ERROR.ALLOCATION_HOURS_NOT_POSITIVE,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.ALLOCATION_HOURS_NOT_POSITIVE]!,
        { quarterHours },
      ),
    );
  }
  if (quarterHours > maxQuarterHours) {
    return err(
      domainError(
        PLANNING_ERROR.ALLOCATION_HOURS_TOO_LARGE,
        'Uma alocação de um dia não pode passar de 24 horas.',
        { quarterHours, max: maxQuarterHours },
      ),
    );
  }
  return ok(quarterHours);
}

/** Converte horas decimais da tela para quartos e valida — `9` e `4.25` entram, `0.1` não. */
export function quarterHoursFromDecimal(hours: number): Result<number, DomainError> {
  const parsed = Hours.fromHours(hours);
  if (!parsed.ok) {
    return err(
      domainError(
        PLANNING_ERROR.ALLOCATION_HOURS_GRANULARITY,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.ALLOCATION_HOURS_GRANULARITY]!,
        { hours },
      ),
    );
  }
  return parseAllocationHours(parsed.value.quarters);
}

export interface CreateWeeklyAllocationInput {
  readonly id: WeeklyAllocationId;
  readonly weeklyPlanId: WeeklyPlanId;
  readonly projectId: ProjectId;
  readonly resourceId: ExecutionResourceId;
  readonly activityId: ActivityId;
  /** O serviço do dia. Ausente só nos três caminhos que não têm como escolher um. */
  readonly serviceId?: ServiceId;
  readonly date: string;
  readonly quarterHours: number;
  readonly exceedsCapacity?: boolean;
  readonly justification?: string;
  readonly acknowledgedRestriction?: boolean;
  readonly notes?: string;
  /** Meta do dia, na unidade da atividade. Quem valida contra a unidade é o caso de uso. */
  readonly plannedQuantity?: number;
  /** A marca da equipe planejada (RN-422). Só o planejamento da equipe a põe. */
  readonly plannedTeamId?: TeamId;
  readonly now: string;
}

export function createWeeklyAllocation(
  input: CreateWeeklyAllocationInput,
): Result<WeeklyAllocation, DomainError> {
  const hours = parseAllocationHours(input.quarterHours);
  if (!hours.ok) return err(hours.error);

  return ok({
    id: input.id,
    weeklyPlanId: input.weeklyPlanId,
    projectId: input.projectId,
    resourceId: input.resourceId,
    activityId: input.activityId,
    date: input.date,
    quarterHours: hours.value,
    ...(input.serviceId === undefined ? {} : { serviceId: input.serviceId }),
    exceedsCapacity: input.exceedsCapacity ?? false,
    version: 1,
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.justification === undefined ? {} : { justification: input.justification }),
    ...(input.acknowledgedRestriction === true ? { acknowledgedRestriction: true } : {}),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
    ...(input.plannedQuantity === undefined ? {} : { plannedQuantity: input.plannedQuantity }),
    ...(input.plannedTeamId === undefined ? {} : { plannedTeamId: input.plannedTeamId }),
  });
}

export interface ChangeAllocationInput {
  readonly quarterHours: number;
  readonly exceedsCapacity?: boolean;
  readonly justification?: string | null;
  readonly notes?: string | null;
  /** `null` limpa a meta; `undefined` deixa como está. */
  readonly plannedQuantity?: number | null;
}

/**
 * Registra o que foi produzido de fato, na conferência.
 *
 * Não é alteração de planejamento e por isso **não passa pela RN-070**: quem escreve
 * quanto saiu não está mudando o plano, está dizendo o que aconteceu com ele. A versão
 * sobe do mesmo jeito, porque outra aba precisa saber que alguém gravou por cima.
 */
export function recordExecutedQuantity(
  allocation: WeeklyAllocation,
  executedQuantity: number | null,
  now: string,
): WeeklyAllocation {
  const { executedQuantity: _current, ...rest } = allocation;
  return {
    ...rest,
    version: allocation.version + 1,
    updatedAt: now,
    ...(executedQuantity === null ? {} : { executedQuantity }),
  };
}

/**
 * Registra quanto do executado não está conforme (Fase 37, RN-475). A conta — a maior das NCs
 * vivas da medição — é de quem grava as NCs; aqui só se guarda. Nada mudando, devolve a mesma.
 */
export function recordNonConformingQuantity(
  allocation: WeeklyAllocation,
  nonConformingQuantity: number | undefined,
  now: string,
): WeeklyAllocation {
  if (allocation.nonConformingQuantity === nonConformingQuantity) return allocation;
  const { nonConformingQuantity: _current, ...rest } = allocation;
  return {
    ...rest,
    version: allocation.version + 1,
    updatedAt: now,
    ...(nonConformingQuantity === undefined ? {} : { nonConformingQuantity }),
  };
}

/**
 * Substitui as horas da alocação.
 *
 * Sempre incrementa a versão, ainda que o valor seja o mesmo: a versão marca "alguém
 * gravou por cima", que é exatamente o que a outra aba precisa detectar.
 */
export function changeAllocationHours(
  allocation: WeeklyAllocation,
  change: ChangeAllocationInput,
  now: string,
): Result<WeeklyAllocation, DomainError> {
  const hours = parseAllocationHours(change.quarterHours);
  if (!hours.ok) return err(hours.error);

  const justification =
    change.justification === undefined
      ? allocation.justification
      : (change.justification ?? undefined);
  const notes = change.notes === undefined ? allocation.notes : (change.notes ?? undefined);
  const plannedQuantity =
    change.plannedQuantity === undefined
      ? allocation.plannedQuantity
      : (change.plannedQuantity ?? undefined);

  const { justification: _j, notes: _n, deletedAt: _d, plannedQuantity: _q, ...rest } = allocation;

  return ok({
    ...rest,
    quarterHours: hours.value,
    exceedsCapacity: change.exceedsCapacity ?? allocation.exceedsCapacity,
    version: allocation.version + 1,
    updatedAt: now,
    ...(justification === undefined ? {} : { justification }),
    ...(notes === undefined ? {} : { notes }),
    ...(plannedQuantity === undefined ? {} : { plannedQuantity }),
  });
}

/** Soma na alocação existente em vez de criar uma segunda (RN-081). */
export function addAllocationHours(
  allocation: WeeklyAllocation,
  quarterHours: number,
  now: string,
  options: {
    exceedsCapacity?: boolean;
    justification?: string;
    /** Meta que vem junto com as horas somadas. Ausente = a meta atual fica como está. */
    addedQuantity?: number;
  } = {},
): Result<WeeklyAllocation, DomainError> {
  const added = parseAllocationHours(quarterHours);
  if (!added.ok) return err(added.error);

  // Duas linhas viram uma: as metas somam como as horas somam. Ficar só com a do destino
  // faria o total do dia mentir para menos — e é esse total que a conferência confere.
  const plannedQuantity =
    options.addedQuantity === undefined
      ? undefined
      : (allocation.plannedQuantity ?? 0) + options.addedQuantity;

  return changeAllocationHours(
    allocation,
    {
      quarterHours: allocation.quarterHours + added.value,
      ...(options.exceedsCapacity === undefined
        ? {}
        : { exceedsCapacity: options.exceedsCapacity }),
      ...(options.justification === undefined ? {} : { justification: options.justification }),
      ...(plannedQuantity === undefined ? {} : { plannedQuantity }),
    },
    now,
  );
}

/**
 * Move a alocação para outro dia.
 *
 * O dia faz parte da **identidade** (RN-081), então mudar de dia muda a chave natural — e
 * pode mudar de plano semanal, quando o destino cai na semana seguinte. Por isso o plano
 * entra como argumento: quem chama já resolveu qual semana recebe, e a entidade não sai
 * procurando.
 *
 * Mover não é alterar horas: a quantidade atravessa intacta. Se o destino já tiver a mesma
 * atividade para a mesma pessoa, quem soma é o caso de uso — a entidade não conhece as
 * outras alocações e não pode decidir isso sozinha.
 */
export function moveAllocationToDay(
  allocation: WeeklyAllocation,
  target: { date: string; weeklyPlanId: WeeklyPlanId },
  now: string,
): Result<WeeklyAllocation, DomainError> {
  if (allocation.deletedAt !== undefined) {
    return err(
      domainError(
        PLANNING_ERROR.ALLOCATION_NOT_FOUND,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.ALLOCATION_NOT_FOUND]!,
      ),
    );
  }

  const parsed = BusinessDate.parse(target.date);
  if (!parsed.ok) return err(parsed.error);

  if (allocation.date === target.date) return ok(allocation);

  return ok({
    ...allocation,
    date: target.date,
    weeklyPlanId: target.weeklyPlanId,
    version: allocation.version + 1,
    updatedAt: now,
  });
}

/** Exclusão é soft delete (RN-088): o registro sai do saldo e continua no histórico. */
export function deleteAllocation(allocation: WeeklyAllocation, now: string): WeeklyAllocation {
  return { ...allocation, deletedAt: now, version: allocation.version + 1, updatedAt: now };
}

/** Alocação excluída não conta em capacidade, saldo nem conflito (RN-049). */
export function countsTowardCapacity(allocation: WeeklyAllocation): boolean {
  return allocation.deletedAt === undefined;
}

/** O lugar do serviço na chave quando não há serviço — ver `weeklyAllocationNaturalKey`. */
const SEM_SERVICO = '-';

/**
 * Chave natural: uma alocação por (plano, recurso, atividade, **serviço**, dia) — RN-231.
 *
 * O dia entra na chave porque a mesma pessoa trabalha na mesma frente em vários dias da
 * semana, e cada dia tem horas próprias. Sem ele, segunda e terça colidiriam e o sistema
 * somaria as duas em um número que não corresponde a dia nenhum.
 *
 * O serviço entrou na Fase 21, e pela razão simétrica: a mesma pessoa faz fôrma e armadura
 * na mesma frente no mesmo dia, e cada uma tem meta em unidade própria. Sem ele, as duas
 * colidiriam e virariam um total de horas sem dono.
 *
 * **Sem serviço é uma chave, não a ausência de uma.** O `-` ocupa o lugar de propósito:
 * duas linhas sem serviço na mesma frente e no mesmo dia continuam sendo a mesma linha, e
 * somam — que é o que a RN-081 sempre disse. Deixar o lugar vazio faria cada gravação criar
 * uma linha nova, e o total do dia passaria a contar a mesma coisa duas vezes.
 */
export function weeklyAllocationNaturalKey(
  weeklyPlanId: WeeklyPlanId,
  resourceId: ExecutionResourceId,
  activityId: ActivityId,
  serviceId: ServiceId | undefined,
  date: string,
): string {
  return `${weeklyPlanId}|${resourceId}|${activityId}|${serviceId ?? SEM_SERVICO}|${date}`;
}

/** Total de horas de uma lista, já descartando o que não conta (RN-049). */
export function totalQuarterHours(allocations: readonly WeeklyAllocation[]): number {
  let total = 0;
  for (const allocation of allocations) {
    if (countsTowardCapacity(allocation)) total += allocation.quarterHours;
  }
  return total;
}
